import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Loader2, X, Lock, Check } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { formatRoleName } from '../../utils/roleUtils';

/**
 * Manage per-document access (Phase 3 of Document Control enhancements).
 *
 * When "Restrict access" is on, only the selected users/roles — plus the
 * author and document-admin roles (enforced server-side) — can read the
 * document. Backend: PUT /api/documents/{id}/permissions.
 */
const DocumentPermissions = ({ open, onClose, docId, users = [], initialRestricted, initialAccessGroups, initialContractorVisible, onSaved }) => {
  const [restricted, setRestricted] = useState(!!initialRestricted);
  const [contractorVisible, setContractorVisible] = useState(!!initialContractorVisible);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [savedMsg, setSavedMsg] = useState('');

  // Roles to choose from = the distinct roles present on tenant users, grouped
  // by display label so legacy/new casing variants (e.g. 'admin' and 'Admin')
  // collapse into ONE checkbox. Selecting it grants every underlying variant,
  // so the saved values still match what the backend compares against.
  const roleGroups = useMemo(() => {
    const map = new Map();
    (users || []).forEach((u) => {
      if (!u.role) return;
      const label = formatRoleName(u.role);
      if (!map.has(label)) map.set(label, new Set());
      map.get(label).add(u.role);
    });
    return Array.from(map.entries())
      .map(([label, vals]) => ({ label, values: Array.from(vals) }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [users]);

  useEffect(() => {
    if (!open) return;
    setRestricted(!!initialRestricted);
    setContractorVisible(!!initialContractorVisible);
    const ag = initialAccessGroups || {};
    setSelectedUsers(ag.users || []);
    setSelectedRoles(ag.roles || []);
    setError('');
    setSavedMsg('');
  }, [open, initialRestricted, initialAccessGroups, initialContractorVisible]);

  const toggle = (list, setList, value) => {
    setSavedMsg('');
    setList(list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);
  };

  const save = async () => {
    setSaving(true);
    setError('');
    setSavedMsg('');
    try {
      const token = localStorage.getItem('token');
      const res = await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/documents/${docId}/permissions`,
        { is_restricted: restricted, users: selectedUsers, roles: selectedRoles, contractor_visible: contractorVisible },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSavedMsg('Access updated.');
      if (onSaved) onSaved(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to update access.');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <div className="flex items-center gap-2">
            <Lock className="w-5 h-5 text-blue-600" />
            <h2 className="text-lg font-bold text-gray-900">Manage Access</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-5">
          {/* Restrict toggle */}
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={restricted}
              onChange={(e) => { setRestricted(e.target.checked); setSavedMsg(''); }}
              className="mt-1"
            />
            <span>
              <span className="font-medium text-gray-900">Restrict access</span>
              <span className="block text-xs text-gray-500">
                When on, only the users/roles selected below (plus the author and admins) can view this document.
                When off, normal role and site visibility applies.
              </span>
            </span>
          </label>

          {/* Contractor audience — contractors only ever see Published
              documents explicitly released to them (enforced server-side). */}
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={contractorVisible}
              onChange={(e) => { setContractorVisible(e.target.checked); setSavedMsg(''); }}
              className="mt-1"
            />
            <span>
              <span className="font-medium text-gray-900">Visible to contractors</span>
              <span className="block text-xs text-gray-500">
                When on, Contractor-role users can view this document once it is Published.
                Off by default — contractors cannot see documents that are not released to them.
              </span>
            </span>
          </label>

          {restricted && (
            <>
              {/* Roles */}
              <div>
                <h3 className="text-sm font-semibold text-gray-800 mb-2">Roles with access</h3>
                {roleGroups.length === 0 ? (
                  <p className="text-xs text-gray-500">No roles found among users.</p>
                ) : (
                  <div className="grid grid-cols-2 gap-1.5 max-h-40 overflow-y-auto">
                    {roleGroups.map((g) => {
                      const checked = g.values.some((v) => selectedRoles.includes(v));
                      return (
                        <label key={g.label} className="flex items-center gap-2 text-sm">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => {
                              setSavedMsg('');
                              setSelectedRoles((prev) => checked
                                ? prev.filter((v) => !g.values.includes(v))
                                : [...prev, ...g.values.filter((v) => !prev.includes(v))]);
                            }}
                          />
                          {g.label}
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Users */}
              <div>
                <h3 className="text-sm font-semibold text-gray-800 mb-2">Users with access</h3>
                <div className="border border-gray-200 rounded-lg max-h-48 overflow-y-auto divide-y divide-gray-100">
                  {(users || []).map((u) => (
                    <label key={u.id} className="flex items-center gap-2 text-sm px-3 py-2 cursor-pointer hover:bg-gray-50">
                      <input
                        type="checkbox"
                        checked={selectedUsers.includes(u.id)}
                        onChange={() => toggle(selectedUsers, setSelectedUsers, u.id)}
                      />
                      <span className="text-gray-900">{u.name || u.email}</span>
                      {u.role && <span className="text-xs text-gray-400">· {formatRoleName(u.role)}</span>}
                    </label>
                  ))}
                  {(users || []).length === 0 && (
                    <p className="text-xs text-gray-500 px-3 py-2">No users available.</p>
                  )}
                </div>
              </div>
            </>
          )}

          {error && <div className="text-sm text-red-600">{error}</div>}
          {savedMsg && (
            <div className="flex items-center gap-1 text-sm text-green-700">
              <Check className="w-4 h-4" /> {savedMsg}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-gray-200 px-6 py-4">
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-700">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Save
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DocumentPermissions;
